import React, { useState, useEffect, useRef } from "react";
// Import GSAP and ScrollTrigger
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Button from "../../components/ui/Button";
import { useNavigate } from "react-router-dom";

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const ProductSection = () => {
  // --- Existing Slider State and Logic ---
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cardsToShow, setCardsToShow] = useState(3);
  // Ref for GSAP animation
  const sectionRef = useRef(null);

  // A helper function to determine the number of cards based on width
  const getCardsToShow = (width) => {
    if (width < 640) {
      return 1;
    } else if (width < 1440) {
      return 2;
    } else {
      return 3;
    }
  };

  const navigate = useNavigate(); // 2. Navigate function initialize karein

  // Click handler function
  const handleNavigation = (path) => {
    if (path) {
      navigate(path);
    } else {
      console.warn("Path define nahi kiya gaya hai!");
    }
  };

  useEffect(() => {
    // --- Window Resize and Slider Logic ---
    setCardsToShow(getCardsToShow(window.innerWidth));

    const handleResize = () => {
      const newCardsToShow = getCardsToShow(window.innerWidth);
      setCardsToShow(newCardsToShow);
      setCurrentSlide(0);
    };

    window.addEventListener("resize", handleResize);

    // --- GSAP Scroll Reveal Animation Logic ---
    const context = gsap.context(() => {
      // 1. Header Text Animation (FERMENTED ORGANIC MANURE)
      const headerLines = gsap.utils.toArray(".product-header-line");

      gsap.from(headerLines, {
        y: 50,
        opacity: 0,
        stagger: 0.15, // Stagger the two lines
        ease: "power2.out",
        scrollTrigger: {
          trigger: headerLines[0].closest("div"), // Use the header container as trigger
          start: "top 85%",
          end: "top 50%",
          scrub: 1,
        },
      });

      // 2. Slider Controls Animation (Fade In)
      const sliderControls = sectionRef.current.querySelector(
        ".slider-controls-container",
      );

      gsap.from(sliderControls, {
        opacity: 0,
        scale: 0.9,
        duration: 1.5,
        ease: "power2.out",
        scrollTrigger: {
          trigger: sliderControls,
          start: "top 80%",
          toggleActions: "play none none reverse", // Play once and reverse when scrolling back up
        },
      });

      // 3. Description Text Animation (Scrubbed Reveal)
      const descriptionText = sectionRef.current.querySelector(
        ".product-description-text",
      );

      if (descriptionText) {
        gsap.set(descriptionText, {
          y: 25,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        gsap.to(descriptionText, {
          y: 0,
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)",
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: descriptionText,
            start: "top 95%",
            end: "center 70%",
            scrub: 1,
          },
        });
      }
    }, sectionRef);

    // 4. Cleanup listener and GSAP context
    return () => {
      window.removeEventListener("resize", handleResize);
      context.revert();
    };
  }, []);
  // ------------------------------------------------------------------------

  const products = [
    {
      id: 1,
      image: "/assets/productImage/img-10.png",
      category: "Fermented Organic Manure (FOM)",
      title: "Organic Manure",
      weight: "Net Weight 30 Kg",
      path: "/UtpannOrganicManure",
    },
    {
      id: 2,
      image: "/assets/productImage/img-11.png",
      category: "Phosphate Rich Organic Manure (PROM)",
      title: "Utpann Kesari Phos",
      weight: "Net Weight 50 Kg",
      path: "/UtpannKesariPhos",
    },
    {
      id: 3,
      image: "/assets/productImage/img-12.png",
      category: "Fermented Organic Manure (FOM)",
      title: "Garden Nest",
      weight: "Net Weight 5 Kg",
      path: "/UtpannGardenNest",
    },
    {
      id: 4,
      image: "/assets/productImage/img-9.png",
      category: "Liquid Fermented Organic Manure (LFOM)",
      title: "Utpann Plant Booster",
      weight: "Net Weight 10ltr / 20ltr / 50ltr",
      path: "/UtpannPlantBooster",
    },

    {
      id: 5,
      image: "/assets/productImage/img-1.png",
      category: "Liquid Fermented Organic Manure (LFOM)",
      title: "Utpann Super",
      weight: "Net Weight 1ltr / 5ltr",
      path: "/UtpannSuper",
    },
    {
      id: 6,
      image: "/assets/productImage/img-2.png",
      category: "Bio-stimulant",
      title: "Utpann PRO",
      weight: "Net Weight 1ltr",
      path: "/UtpannPRO",
    },
    {
      id: 7,
      image: "/assets/productImage/img-8.png",
      category: "Mycorrhizal Bio Fertilizer",
      title: "Utpann U-max",
      weight: "Net Weight 2kg / 4kg / 30kg",
      path: "/UtpannUMax",
    },
    {
      id: 8,
      image: "/assets/productImage/img-3.png",
      category: "Plant Immunity Booster",
      title: "Utpann Defensio",
      weight: "Net Weight 1ltr",
      path: "/UtpannDefensio",
    },
    {
      id: 9,
      image: "/assets/productImage/img-4.png",
      category: "Zinc Solubilizing Bacteria",
      title: "Utpann ZSB",
      weight: "Net Weight 500gm",
      path: "/UtpannZSB",
    },
    {
      id: 10,
      image: "/assets/productImage/img-5.png",
      category: "Mycorrhizal Bio Fertilizer",
      title: "Utpann MYCO",
      weight: "Net Weight 100gm",
      path: "/UtpannMYCO",
    },
    {
      id: 11,
      image: "/assets/productImage/img-6.png",
      category: "Carrier Based Consortia",
      title: "Utpann CBC-19",
      weight: "Net Weight 500gm / 1kg",
      path: "/UtpannCBC-19",
    },
    {
      id: 12,
      image: "/assets/productImage/img-7.png",
      category: "Phosphate Solubilizing Fungal",
      title: "Utpann PSF",
      weight: "Net Weight 500gm",
      path: "/product-details12",
    },

    // ... बाकी डेटा यहाँ रखें
  ];

  // Calculate the maximum index we can slide to, based on the dynamic cardsToShow
  const maxSlide = products.length - cardsToShow;

  const nextSlide = () => {
    if (currentSlide < maxSlide) {
      setCurrentSlide((prev) => prev + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
    }
  };

  // Calculate the percentage to translate for one card move (100% / cardsToShow)
  const slidePercentage = 100 / cardsToShow;

  return (
    <section
      ref={sectionRef} // Attach the ref here
      className="w-full py-[20px] sm:py-[25px] xl:py-[34px]"
    >
      <div className="w-full max-w-[1714px] mx-auto px-4 sm:px-6 xl:px-[56px]">
        {/* Section Header */}
        <div className="text-center mb-[30px] sm:mb-[40px] xl:mb-[66px] space-y-1 xl:space-y-[6px]">
          <div className="space-y-2 xl:space-y-0 xl:-space-y-8">
            <h2 className="product-header-line text-[40px] sm:text-[60px] uppercase xl:text-2xl font-bold  text-secondary-text font-['Parkinsans']">
              Explore
            </h2>
            <h3 className="product-header-line text-[40px] sm:text-[60px] uppercase xl:text-2xl font-bold  text-secondary-text font-['Parkinsans']">
              Our Products
            </h3>
          </div>
        </div>

        {/* Product Slider Container - Added class for GSAP */}
        <div className="flex items-center justify-center gap-4 xl:gap-8 mb-[40px] sm:mb-[60px] xl:mb-[80px] slider-controls-container">
          {/* Previous Button */}
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className={`flex-shrink-0 w-[50px] h-[50px] xl:w-[80px] xl:h-[80px] border border-secondary-text rounded-2xl xl:rounded-[40px] bg-white transition-colors duration-300 flex items-center justify-center ${
              currentSlide === 0
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-50"
            }`}
            aria-label="Previous product"
          >
            <img src="/assets/left.png" alt="Previous" />
          </button>

          {/* Products Viewport */}
          <div className="flex-1 max-w-[1200px] xl:max-w-[1600px] overflow-hidden">
            {/* Slides Track */}
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${currentSlide * slidePercentage}%)`,
              }}
            >
              {products?.map((product) => (
                <div
                  key={product?.id}
                  className={`flex-shrink-0 flex justify-center px-4 ${
                    cardsToShow === 1
                      ? "w-full"
                      : cardsToShow === 2
                        ? "w-1/2"
                        : "w-1/3"
                  }`}
                >
                  <div className="w-full   hover:border-[#B7DB83]  border border-secondary-text rounded-[30px] bg-white flex flex-col items-center justify-center p-6 sm:p-8 xl:p-[30px]">
                    <img
                      src={product?.image}
                      alt={product?.alt}
                      className="w-full h-full object-contain"
                    />
                    <div>
                      <p className="text-[16px] sm:text-[20px] xl:text-[24px] text-center  font-bold text-[#343163] mt-4">
                        {product?.title}
                      </p>
                      <p className="text-[14px] sm:text-[18px] xl:text-[20px] text-center font-light text-primary-text mt-1">
                        {product?.weight}
                      </p>
                      <div className="mt-[21px]">
                        <Button
                          text="Learn More"
                          text_font_size="text-[16px] sm:text-[20px] xl:text-[24px]"
                          text_font_family="Parkinsans"
                          text_font_weight="font-semibold"
                          text_line_height="leading-xl"
                          text_text_align="center"
                          border_border="1px solid #343163"
                          border_border_radius="rounded-2xl"
                          padding="t=12px,r=32px,b=12px,l=32px"
                          onClick={() => handleNavigation(product?.path)}
                          layout_width="100%"
                          className="bg-transparent text-[#343163]  hover:bg-[#343163] hover:text-white  transition-all duration-200"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Next Button */}
          <button
            onClick={nextSlide}
            disabled={currentSlide >= maxSlide}
            className={`flex-shrink-0 w-[50px] h-[50px] xl:w-[80px] xl:h-[80px] border border-secondary-text rounded-2xl xl:rounded-[40px] bg-white transition-colors duration-300 flex items-center justify-center ${
              currentSlide >= maxSlide
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-50"
            }`}
            aria-label="Next product"
          >
            <img src="/assets/right.png" alt="Next" />
          </button>
        </div>

        {/* Description */}
        <div className="text-center">
          {/* Added class for GSAP */}
          <p className="product-description-text text-[20px] xl:text-sm font-light leading-loose text-primary-text mx-auto">
            UTPANN Organic Fertilizer enriches soil naturally, improves crop
            productivity, and serves as a sustainable alternative to traditional
            cow-dung manure. Produced through scientific waste management at
            Asia’s largest bio-gas plant, it supports cleaner surroundings while
            promoting healthier farming.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ProductSection;
